import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SimpleBar from "simplebar-react";
import useCustomFecthQuery from "../../../Hooks/useCustomFecthQuery";
import { detectDeviceType } from "../../../utils/deviceType";
import About from "./One/About";
import Schedule from "./One/Schedule";
import Sponsors from "./One/Sponsors";
import OurSpeaker from "./One/Speakers";
import Faq from "./One/Faq";
import EventMap from "./One/EventMap";
import HouseRules from "../EventLobby/One/HouseRules"; // 
import eventPlaceHolder from "../../../assets/images/placeholder/EventPplaceholder.png";
import { ArrowLeft, Menu, X } from "lucide-react";

const EventOne = ({ eventDetails, token, eventID }) => {
  const navigate = useNavigate();
  const deviceType = detectDeviceType();
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 480;

  const {
    data: getMySpeakers,
    error: errorGetMySpeakers,
    isLoading: isLoadingGetMySpeakers,
  } = useCustomFecthQuery(
    ["mySpeakers", eventID],
    `${process.env.REACT_APP_API}speakers/event/${eventID}`,
    token,
    { enabled: !!eventID }
  );

  const {
    data: getMySessions,
    error: errorGetMySessions,
    isLoading: isLoadingGetMySessions,
  } = useCustomFecthQuery(
    ["mySessions", eventID],
    `${process.env.REACT_APP_API}sessions/event/${eventID}`,
    token,
    { enabled: !!eventID }
  );

  const {
    data: getMySponsors,
    error: errorGetMySponsors,
    isLoading: isLoadingGetMySponsors,
  } = useCustomFecthQuery(
    ["mySponsors", eventID],
    `${process.env.REACT_APP_API}sponsors/event/${eventID}`,
    token,
    { enabled: !!eventID }
  );

  const {
    data: getMyBooths,
    error: errorGetMyBooths,
    isLoading: isLoadingGetMyBooths,
  } = useCustomFecthQuery(
    ["myBooths", eventID],
    `${process.env.REACT_APP_API}booths/event/${eventID}`,
    token,
    { enabled: !!eventID }
  );

  // ✅ Check if event has speakers
  const hasSpeakers = getMySpeakers?.status === "ok" && getMySpeakers?.data?.length > 0;

  const handleGoBack = () => {
    navigate("/dashboard");
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // ✅ Helper: Generate event entry URL based on event type
  const getEventEntryPath = () => {
    if (!eventDetails || !eventID) return `/dashboard/event/${eventID}`;
    
    // Sanitize event name for URL (lowercase, replace spaces/special chars with hyphens)
    const eventName = eventDetails?.name
      ? eventDetails.name
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')  // Remove special characters
          .replace(/\s+/g, '-')           // Replace spaces with hyphens
          .replace(/-+/g, '-')            // Remove duplicate hyphens
          .trim()
      : 'event';
    
    // Get event type (fallback to 'webinar' as default)
    const eventType = eventDetails?.type?.toLowerCase() || 'webinar';
    
    // Map event types to route patterns from routes.js
    switch (eventType) {
      case 'conference':
        return `/${eventName}/conference/${eventID}`;
      case 'exhibition':
        return `/${eventName}/exhibition/${eventID}`;
      case 'webinar':
        return `/${eventName}/webinar/${eventID}`;
      case 'hybrid':
        // Hybrid events can use conference or webinar route
        return `/${eventName}/conference/${eventID}`;
      default:
        // Fallback to generic event landing page
        return `/${eventName}/event-landing/${eventID}`;
    }
  };

  // ✅ Handler for Enter Event button
  const handleEnterEvent = () => {
    const targetPath = getEventEntryPath();
    console.log("Navigating to event:", targetPath); // Debug log
    navigate(targetPath);
  };

  // Calculate dynamic heights for mobile
  const getSimpleBarHeight = () => {
    if (isSmallMobile) return "calc(100vh - 120px)";
    if (isMobile) return "calc(100vh - 100px)";
    return "83vh";
  };

  // Scroll to section handler
  const scrollToSection = (sectionId) => {
    closeMenu();
    const element = document.getElementById(sectionId.toLowerCase());
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <React.Fragment>
      <div className="new-event-landing-page" style={styles.container}>
        {/* Unified Header Container */}
        <div style={styles.headerContainer}>
          {/* Left: Back Button */}
          <button
            onClick={handleGoBack}
            style={styles.backButton}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#f3f4f6";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
            }}
            title="Go back to dashboard"
            type="button"
          >
            <ArrowLeft size={isMobile ? 18 : 18} />
            {!isSmallMobile && <span>Back</span>}
          </button>

          {/* Center: Navigation Menu (Desktop) */}
          {!isMobile && (
            <div style={styles.desktopNav}>
              <nav style={styles.desktopNavList}>
                <button
                  onClick={() => scrollToSection("about")}
                  style={styles.desktopNavItem}
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection("schedule")}
                  style={styles.desktopNavItem}
                >
                  Schedule
                </button>
                {/* ✅ Conditional Speakers Link */}
                {hasSpeakers && (
                  <button
                    onClick={() => scrollToSection("speakers")}
                    style={styles.desktopNavItem}
                  >
                    Speakers
                  </button>
                )}
              </nav>
            </div>
          )}

          {/* Right: Desktop Enter Event Button & Mobile Hamburger */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {!isMobile && (
              <button
                onClick={handleEnterEvent}
                style={styles.desktopEnterButton}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(246, 138, 32, 0.4)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(246, 138, 32, 0.3)";
                }}
              >
                Enter Event
              </button>
            )}

            {isMobile && (
              <button
                onClick={toggleMenu}
                style={styles.hamburgerButton}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
                title={isMenuOpen ? "Close menu" : "Open menu"}
                type="button"
              >
                {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Navigation Menu Overlay */}
        {isMobile && isMenuOpen && (
          <div style={styles.mobileMenuOverlay} onClick={closeMenu}>
            <div style={styles.mobileMenuContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.mobileMenuHeader}>
                <span style={styles.mobileMenuTitle}>Menu</span>
                <button
                  onClick={closeMenu}
                  style={styles.mobileMenuClose}
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>
              
              {/* Mobile Navigation Items */}
              <div style={styles.mobileMenuBody}>
                <nav style={styles.mobileNavList}>
                  <button
                    onClick={() => scrollToSection("about")}
                    style={styles.mobileNavItem}
                  >
                    About
                  </button>
                  <button
                    onClick={() => scrollToSection("schedule")}
                    style={styles.mobileNavItem}
                  >
                    Schedule
                  </button>
                  {/* ✅ Conditional Speakers Link (Mobile) */}
                  {hasSpeakers && (
                    <button
                      onClick={() => scrollToSection("speakers")}
                      style={styles.mobileNavItem}
                    >
                      Speakers
                    </button>
                  )}
                </nav>

                {/* Enter Event Button */}
                <button
                  onClick={() => {
                    closeMenu();
                    handleEnterEvent();
                  }}
                  style={styles.mobileEnterButton}
                >
                  Enter Event
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="w-100" style={styles.contentWrapper}>
          <SimpleBar style={{ height: getSimpleBarHeight() }}>
            {/* Banner */}
            <div className="new-event-landing-page-banner" style={styles.banner}>
              <img
                src={
                  eventDetails?.bannerImage
                    ? eventDetails?.bannerImage
                    : eventPlaceHolder
                }
                alt="Event Banner"
                style={styles.bannerImage}
              />
            </div>
            
            {/* About Section */}
            <div id="about" style={styles.section}>
              <About eventDetails={eventDetails} />
            </div>
            
            {/* Schedule Section */}
            {getMySessions?.status === "ok" &&
            getMySessions?.data?.length === 0 ? null : (
              <div id="schedule" style={styles.section}>
                <Schedule
                  eventDetails={eventDetails}
                  token={token}
                  eventID={eventID}
                />
              </div>
            )}
            
            {/* ✅ Speakers Section with proper centering */}
            {hasSpeakers && (
              <div 
                id="speakers" 
                className="speakers-section"
                style={{
                  ...styles.speakersSection,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <OurSpeaker
                  token={token}
                  eventID={eventID}
                  getMySpeakers={getMySpeakers}
                />
              </div>
            )}
            
            {/* Sponsors Section */}
            {getMySponsors?.status === "ok" &&
            getMySponsors?.data?.length === 0 ? null : (
              <div style={styles.section}>
                <Sponsors token={token} eventID={eventID} />
              </div>
            )}
            
            {/* FAQ Section */}
            {eventDetails?.faq?.length > 0 ? (
              <div id="faq" style={styles.section}>
                <Faq
                  token={token}
                  eventID={eventID}
                  eventDetails={eventDetails}
                />
              </div>
            ) : null}

            {/* ✅ House Rules Section - ADDED */}
            <div style={styles.section}>
              <HouseRules eventDetails={eventDetails} />
            </div>
            
            {/* Event Map Section */}
            {eventDetails?.venueMap ? (
              <div style={styles.section}>
                <EventMap venueMap={eventDetails?.venueMap} />
              </div>
            ) : null}
          </SimpleBar>
        </div>
      </div>

      {/* CSS Animations and Mobile Overrides */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        @media (max-width: 768px) {
          .new-event-landing-page-banner img {
            height: auto !important;
            max-height: 250px !important;
            object-fit: cover !important;
          }
          
          [style*="1000px"],
          [style*="width: 1000px"] {
            width: 100% !important;
            max-width: 100% !important;
          }

          /* Fix session text overflow */
          .session-info,
          .session-date,
          .session-time,
          [class*="session"] {
            word-wrap: break-word !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
            white-space: normal !important;
            text-align: center !important;
            max-width: 100% !important;
            padding: 0 8px !important;
          }

          /* ✅ Make speakers section responsive and centered */
          .speakers-section,
          [class*="speaker"] {
            width: 100% !important;
            max-width: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
            padding: 0 12px !important;
          }

          /* ✅ Center the speaker card container */
          .speaker-card-container,
          [class*="speaker-card"],
          .col-md-4,
          .col-lg-3,
          [class*="col-"] {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
            width: 100% !important;
            max-width: 280px !important;
            margin: 0 auto !important;
            float: none !important;
          }

          /* ✅ Center speaker image container */
          .speaker-image-wrapper,
          [class*="speaker-image"],
          .speaker-img-container {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: 0 auto !important;
            width: 100% !important;
          }

          /* ✅ Center speaker images */
          .speaker-image,
          [class*="speaker"] img,
          img[class*="speaker"] {
            width: 120px !important;
            height: 120px !important;
            margin: 0 auto !important;
            border-radius: 50% !important;
            display: block !important;
          }

          /* ✅ Center speaker content */
          .speaker-content,
          [class*="speaker-content"],
          .speaker-details {
            text-align: center !important;
            width: 100% !important;
            max-width: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
          }

          /* ✅ Center speaker name */
          .speaker-name,
          [class*="speaker-name"],
          h4[class*="speaker"],
          h5[class*="speaker"] {
            text-align: center !important;
            width: 100% !important;
            margin: 0 auto !important;
          }

          /* ✅ Center all text in speaker section */
          .speaker-section p,
          .speaker-section span,
          .speaker-section div {
            text-align: center !important;
          }

          /* Fix any text that might overflow */
          p, span, div {
            max-width: 100%;
            overflow-wrap: break-word;
          }
        }

        @media (max-width: 480px) {
          .new-event-landing-page-banner img {
            max-height: 200px !important;
          }
          
          h1, h2, h3 {
            font-size: 1.25rem !important;
          }
          
          p {
            font-size: 0.9rem !important;
          }

          /* ✅ Extra mobile optimizations for speakers */
          .speaker-card-container,
          .col-md-4,
          .col-lg-3 {
            max-width: 240px !important;
            margin: 0 auto !important;
          }

          .speaker-image,
          [class*="speaker"] img {
            width: 100px !important;
            height: 100px !important;
          }
        }

        /* ✅ Global centering for speaker section */
        #speakers {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          text-align: center !important;
        }

        #speakers > * {
          text-align: center !important;
        }

        /* ✅ House Rules Styles */
        .house-rules-container {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          justify-content: center;
          padding: 10px;
        }
        .rule-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 12px;
          min-width: 200px;
          max-width: 250px;
        }
        .houseDivider {
          width: 60px;
          height: 2px;
          background: #f68a20;
          margin: 10px 0;
        }
        .rule-card .text {
          font-size: 14px;
          color: #333;
          line-height: 1.4;
        }
      `}</style>
    </React.Fragment>
  );
};

// Professional Styles
const styles = {
  container: {
    width: "100%",
    maxWidth: "100%",
    minHeight: "100vh",
    backgroundColor: "#f8f9fa",
    position: "relative",
    overflowX: "hidden",
  },
  headerContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "10px 14px",
    backgroundColor: "white",
    color: "#1f2937",
    border: "none",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "14px",
    transition: "all 0.2s ease",
    flexShrink: 0,
    minWidth: "80px",
  },
  desktopNav: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  desktopNavList: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  desktopNavItem: {
    background: "none",
    border: "none",
    fontSize: "15px",
    fontWeight: "600",
    color: "#f68a20",
    cursor: "pointer",
    padding: "8px 12px",
    transition: "all 0.2s ease",
  },
  desktopEnterButton: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #f68a20 0%, #fb923c 100%)",
    color: "white",
    border: "none",
    borderRadius: "25px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(246, 138, 32, 0.3)",
    transition: "all 0.2s ease",
  },
  hamburgerButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "60px",
    height: "60px",
    color: "#000000",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0,
  },
  mobileMenuOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1001,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    animation: "fadeIn 0.3s ease",
  },
  mobileMenuContent: {
    width: "280px",
    maxWidth: "85%",
    backgroundColor: "white",
    minHeight: "100vh",
    boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.15)",
    animation: "slideIn 0.3s ease",
    overflow: "hidden",
  },
  mobileMenuHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
  },
  mobileMenuTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#2d1818",
  },
  mobileMenuClose: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "60px",
    height: "60px",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
    transition: "all 0.2s ease",
  },
  mobileMenuBody: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  mobileNavList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  mobileNavItem: {
    padding: "14px 20px",
    background: "#f9fafb",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#374151",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.2s ease",
  },
  mobileEnterButton: {
    width: "100%",
    padding: "14px 20px",
    background: "linear-gradient(135deg, #f68a20 0%, #fb923c 100%)",
    color: "white",
    border: "none",
    borderRadius: "25px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(246, 138, 32, 0.3)",
    marginTop: "8px",
  },
  contentWrapper: {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    paddingTop: "70px",
  },
  banner: {
    width: "100%",
    overflow: "hidden",
  },
  bannerImage: {
    width: "100%",
    height: "auto",
    display: "block",
    objectFit: "cover",
    maxHeight: "400px",
  },
  section: {
    padding: "24px 16px",
    backgroundColor: "white",
    marginBottom: "16px",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
  },
  speakersSection: {
    padding: "24px 16px",
    backgroundColor: "white",
    marginBottom: "16px",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
  },
};

export default EventOne;